import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";
import AuthLangSwitch from "../components/AuthLangSwitch";
import { useOffline } from "../lib/offline";

const LOGO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_concierge-hub-62/artifacts/c192v2ok_logo%20Weissblom%20nieuw%20juli%202026%20%28Adobe%20Garamond%20Pro%29_diap.webp";

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { refresh } = useAuth();
  const { online } = useOffline();
  const nav = useNavigate();
  const location = useLocation();
  // Feb 2026 · DSGVO/Gast-Tier (5 Sep 2026): Nach erfolgreicher Signup-
  // Registrierung navigiert die Signup-Seite mit `state.pendingEmail`
  // hierher. Wir ueberspringen dann Schritt 1 (E-Mail-Eingabe) und
  // erwarten sofort den OTP-Code, den die Signup-Route schon per Mail
  // versendet hat.
  const pendingEmail = location.state?.pendingEmail;

  const [step, setStep] = useState(pendingEmail ? 2 : 1);
  const [email, setEmail] = useState(pendingEmail || "");
  const [code, setCode] = useState(Array(8).fill(""));
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (step === 2) inputRefs.current[0]?.focus();
  }, [step]);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!online) {
      toast.error(lang === "en" ? "An internet connection is required to sign in." : "Für die Anmeldung ist eine Internetverbindung erforderlich.");
      return;
    }
    const clean = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(clean)) {
      toast.error(lang === "en" ? "Please enter a valid email address." : "Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { email: clean });
      toast.success(t.login.codeSent);
      setStep(2);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDigit = (idx, val) => {
    const clean = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = clean;
    setCode(next);
    if (clean && idx < 7) inputRefs.current[idx + 1]?.focus();
  };

  const handleKey = (idx, e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 8);
    if (pasted.length === 0) return;
    e.preventDefault();
    const next = Array(8).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCode(next);
    inputRefs.current[Math.min(pasted.length, 7)]?.focus();
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!online) {
      toast.error(lang === "en" ? "An internet connection is required to sign in." : "Für die Anmeldung ist eine Internetverbindung erforderlich.");
      return;
    }
    const joined = code.join("");
    if (joined.length !== 8) {
      toast.error(lang === "en" ? "Please enter the 8-digit code." : "Bitte 8-stelligen Code eingeben.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: email.trim().toLowerCase(), code: joined });
      // Auth token is stored server-side via httpOnly cookie (set-cookie in the response).
      // Never persist it in localStorage / JS-accessible storage (XSS-safe).
      toast.dismiss();
      await refresh();
      const role = data.user?.role;
      nav(role === "admin" || role === "concierge" ? "/team" : "/app", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      {/* Left — Navy logo panel */}
      <div className="hidden md:flex flex-col justify-between p-12 lg:p-16 text-ivory relative" style={{ background: BRAND_COLORS.navy }}>
        <div className="flex items-center gap-3" data-testid="login-brand">
          <img src="/weissblom-logo.webp" alt="Weissblom" className="h-16 w-auto" style={{ objectFit: "contain" }} />
        </div>

        <div className="max-w-md">
          <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] text-ivory mb-6">
            {lang === "en" ? <>Your home,<br/>in the best hands.</> : <>Ihr Zuhause,<br/>in besten Händen.</>}
          </h1>
        </div>

        <div className="flex items-center justify-between">
          <div className="label-caps text-ivory/50">Private Property Concierge</div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-ivory/60">
            <button data-testid="lang-de" onClick={() => setLang("de")} className={lang === "de" ? "text-gold" : ""}>DE</button>
            <span className="mx-2 opacity-40">/</span>
            <button data-testid="lang-en" onClick={() => setLang("en")} className={lang === "en" ? "text-gold" : ""}>EN</button>
          </div>
        </div>
      </div>

      {/* Right — Ivory form panel */}
      <div className="relative flex items-center justify-center p-8 md:p-16" style={{ background: BRAND_COLORS.ivory }}>
        <AuthLangSwitch className="absolute top-6 right-6 md:top-8 md:right-10 z-10" />
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-3 mb-10 p-4" style={{ background: BRAND_COLORS.navy }}>
            <img src="/weissblom-logo.webp" alt="Weissblom" className="h-10 w-auto" style={{ objectFit: "contain" }} />
          </div>

          <div className="mb-10">
            <div className="label-caps mb-3">{lang === "en" ? "Weissblom Members" : "Weissblom Mitglieder"}</div>
            <h2 className="font-display text-4xl md:text-5xl text-navy mb-3">{t.login.title}</h2>
            <p className="text-[14px] text-navy-dark/70 leading-relaxed">{t.login.lead}</p>
          </div>

          {step === 1 ? (
            <form onSubmit={requestCode} noValidate className="space-y-6" data-testid="login-form-email">
              <div>
                <label className="label-caps block mb-2">{t.login.emailLabel}</label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.login.emailPlaceholder}
                  className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark placeholder:text-navy/30"
                />
              </div>
              <button
                data-testid="login-request-code-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-navy text-ivory py-3.5 text-[13px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-50"
                style={{ background: BRAND_COLORS.navy }}
              >
                {loading ? t.common.loading : t.login.requestCode}
              </button>
              <p className="text-[12px] text-navy-dark/50 leading-relaxed pt-4 border-t border-navy/10">
                {t.login.inviteHint}
              </p>
              <p className="text-center text-[12px] text-navy-dark/70 pt-1">
                {lang === "en" ? "New to Weissblom?" : "Neu bei Weissblom?"}{" "}
                <Link to="/signup" data-testid="login-to-signup-link" className="text-navy underline hover:text-gold">
                  {lang === "en" ? "Sign up free" : "Kostenlos registrieren"}
                </Link>
              </p>
              <div className="pt-2 flex gap-4 flex-wrap">
                <a href="/impressum" data-testid="login-impressum-link"
                  className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-gold transition-colors">
                  {lang === "en" ? "Imprint" : "Impressum"}
                </a>
                <a href="/datenschutz" data-testid="login-datenschutz-link"
                  className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-gold transition-colors">
                  {lang === "en" ? "Privacy" : "Datenschutz"}
                </a>
              </div>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-6" data-testid="login-form-code">
              <div>
                <div className="label-caps mb-3">{t.login.codeLabel}</div>
                <div className="flex gap-2 justify-between" onPaste={handlePaste}>
                  {code.map((c, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      data-testid={`otp-input-${i}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={c}
                      onChange={(e) => handleDigit(i, e.target.value)}
                      onKeyDown={(e) => handleKey(i, e)}
                      className="w-11 h-14 md:w-12 md:h-16 text-center text-2xl font-display text-navy bg-white border border-navy/20 focus:border-gold focus:outline-none"
                    />
                  ))}
                </div>
                <p className="mt-3 text-[12px] text-navy-dark/60">
                  {t.login.codeSent} <span className="text-navy">{email}</span>
                </p>
              </div>
              <button
                data-testid="login-verify-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-navy text-ivory py-3.5 text-[13px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-50"
                style={{ background: BRAND_COLORS.navy }}
              >
                {loading ? t.common.loading : t.login.verify}
              </button>
              <button
                data-testid="login-back-btn"
                type="button"
                onClick={() => { setStep(1); setCode(Array(8).fill("")); }}
                className="w-full text-[12px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy transition-colors"
              >
                {t.login.back}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
